import React from 'react';
import { GraduationCap, Building2, Heart, Scale, CheckCircle, Users, Globe, Target, FileText, Award, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { mockData } from '../data/mockData';

export const Pilares = () => {
  const { pillars } = mockData;

  const pillarDetails = {
    1: {
      icon: GraduationCap,
      color: "from-blue-50 to-indigo-50",
      accentColor: "text-blue-700",
      bgColor: "bg-blue-100",
      programs: [
        "Programas curriculares de promoción de la salud",
        "Actividades co-curriculares de bienestar",
        "Talleres de habilidades psicosociales",
        "Formación en liderazgo estudiantil"
      ],
      meta: "Que cada estudiante desarrolle competencias psicosociales y hábitos saludables durante su formación universitaria."
    },
    2: {
      icon: Building2,
      color: "from-green-50 to-emerald-50",
      accentColor: "text-green-700",
      bgColor: "bg-green-100",
      programs: [
        "Espacios libres de humo y alcohol",
        "Áreas verdes y de esparcimiento",
        "Infraestructura accesible e inclusiva",
        "Programa de campus sostenible"
      ],
      meta: "Que la universidad sea un entorno saludable en lo físico, social, ambiental y emocional."
    },
    3: {
      icon: Heart,
      color: "from-pink-50 to-rose-50",
      accentColor: "text-rose-700",
      bgColor: "bg-rose-100",
      programs: [
        "Centro de servicios médicos",
        "Programas de alimentación saludable",
        "Centros deportivos y recreativos",
        "Programas de bienestar mental"
      ],
      meta: "Garantizar servicios integrales de salud y fomentar hábitos de vida activa y alimentación saludable en toda la comunidad universitaria."
    },
    4: {
      icon: Scale,
      color: "from-purple-50 to-violet-50",
      accentColor: "text-purple-700",
      bgColor: "bg-purple-100",
      programs: [
        "Marco normativo institucional",
        "Participación en consejos de salud",
        "Alineación con ODS y cartas internacionales",
        "Consejos institucionales de promoción de la salud"
      ],
      meta: "Que las universidades acreditadas no solo sean promotoras de salud internamente, sino también actores clave en la formulación de políticas públicas de salud y bienestar."
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-burgundy-50 to-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-burgundy-50/65 to-white/75 z-10"></div>
          <img
            src="https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
            alt="Campus universitario sostenible"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center">
            <div className="inline-flex items-center bg-white/80 backdrop-blur-sm text-burgundy-800 px-6 py-3 rounded-full text-sm font-medium mb-6 shadow-md">
              🏛️ Cuatro Pilares Fundamentales
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-burgundy-900 mb-6">
              Pilares del Modelo UPS
            </h1>
            <p className="text-xl text-burgundy-700 mb-8 max-w-3xl mx-auto">
              Cuatro ejes fundamentales que sustentan una universidad promotora de la salud integral
            </p>
            
            {/* Visual indicators */}
            <div className="flex flex-wrap justify-center gap-3 mt-8 max-w-4xl mx-auto">
              <div className="flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full">
                <GraduationCap className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Educación</span>
              </div>
              <div className="flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full">
                <Building2 className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Entornos</span>
              </div>
              <div className="flex items-center bg-rose-100 text-rose-800 px-4 py-2 rounded-full">
                <Heart className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Servicios</span>
              </div>
              <div className="flex items-center bg-purple-100 text-purple-800 px-4 py-2 rounded-full">
                <Scale className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Políticas</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg className="w-full h-12 text-white" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Detailed Pillars */}
      {pillars.map((pillar, index) => {
        const details = pillarDetails[pillar.id];
        const IconComponent = details.icon;
        const isReversed = index % 2 !== 0;

        return (
          <section key={pillar.id} className={`py-16 bg-gradient-to-r ${details.color}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isReversed ? 'lg:grid-flow-col-dense' : ''}`}>

                {/* Content */}
                <div className={isReversed ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className={`w-16 h-16 ${details.bgColor} rounded-full flex items-center justify-center mr-4`}>
                      <IconComponent className={`h-8 w-8 ${details.accentColor}`} />
                    </div>
                    <div className="bg-burgundy-700 text-white rounded-full w-8 h-8 flex items-center justify-center">
                      <span className="font-bold">{pillar.id}</span>
                    </div>
                  </div>

                  <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
                    {pillar.title}
                  </h2>

                  <p className="text-lg text-gray-700 mb-8">
                    {pillar.description}
                  </p>

                  <div className="space-y-4 mb-8">
                    <h3 className="text-xl font-semibold text-burgundy-800">
                      Componentes Clave:
                    </h3>
                    <ul className="space-y-3">
                      {pillar.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Meta del Pilar */}
                  <div className="bg-white/70 backdrop-blur-sm p-6 rounded-lg border border-gray-200 mb-6">
                    <div className="flex items-start space-x-3">
                      <Target className={`h-5 w-5 ${details.accentColor} mt-1 flex-shrink-0`} />
                      <div>
                        <h4 className="text-lg font-semibold text-burgundy-800 mb-2">Meta del Pilar:</h4>
                        <p className="text-gray-700 font-medium">
                          🔹 {details.meta}
                        </p>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Pillar Image */}
                <div className={isReversed ? 'lg:col-start-1' : ''}>
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl h-96">
                    {/* Imagen específica por pilar */}
                    <img
                      src={
                        pillar.id === 1 
                          ? "https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                        : pillar.id === 2 
                          ? "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                        : pillar.id === 3 
                          ? "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                        : "https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                      }
                      alt={pillar.title}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Overlay gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${details.color} opacity-85`}></div>
                    
                    {/* Content overlay */}
                    <div className="absolute inset-0 p-6 flex flex-col justify-center items-center text-center">
                      <div className={`w-20 h-20 ${details.bgColor} rounded-full flex items-center justify-center mb-6 shadow-lg`}>
                        <IconComponent className={`h-10 w-10 ${details.accentColor}`} />
                      </div>
                      
                      <h4 className="text-2xl font-bold text-burgundy-800 mb-4">
                        Programas y Servicios
                      </h4>
                      
                      <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 max-w-sm">
                        <ul className="space-y-2 text-sm text-gray-700">
                          {details.programs.map((program, idx) => (
                            <li key={idx} className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                              <span className="text-left">{program}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Pillar number badge */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-burgundy-700 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                      {pillar.id}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Articulación Multisectorial */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Articulación Multisectorial y Multidisciplinaria
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8">
              Los cuatro ejes no funcionan de manera aislada, sino en articulación multisectorial y
              multidisciplinaria, garantizando que la acreditación de una universidad como UPS refleje
              un compromiso integral, sostenible y con incidencia social.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <Card className="bg-white border-l-4 border-l-blue-500 relative overflow-hidden">
              <div className="absolute inset-0 opacity-5">
                <img
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                  alt="Multisectorial"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader className="text-center relative z-10">
                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <CardTitle className="text-burgundy-800">Multisectorial</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-600 text-center mb-4">
                  Integración de múltiples sectores para maximizar el impacto
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Sector académico</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Sector salud</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Sector gubernamental</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Organizaciones internacionales</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-l-4 border-l-green-500 relative overflow-hidden">
              <div className="absolute inset-0 opacity-5">
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                  alt="Multidisciplinaria"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader className="text-center relative z-10">
                <Globe className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <CardTitle className="text-burgundy-800">Multidisciplinaria</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-600 text-center mb-4">
                  Conocimientos diversos para abordar desafíos complejos
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Ciencias de la salud</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Educación y pedagogía</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Políticas públicas</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Sostenibilidad ambiental</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-l-4 border-l-purple-500 relative overflow-hidden">
              <div className="absolute inset-0 opacity-5">
                <img
                  src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                  alt="Incidencia Social"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader className="text-center relative z-10">
                <Target className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <CardTitle className="text-burgundy-800">Incidencia Social</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-600 text-center mb-4">
                  Compromiso integral y sostenible con la sociedad
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Políticas públicas saludables</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Transformación social</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Desarrollo sostenible</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Equidad en salud</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Implementación Detallada del 4º Pilar */}
      <section className="py-16 bg-gradient-to-r from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Implementación Integral del Programa
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              Institucionalización de políticas, financiamiento, gobernanza, marco normativo y
              articulación con agendas globales de salud
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-2xl font-bold text-burgundy-800 mb-6">
                Estrategias de Implementación
              </h3>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-l-purple-500">
                  <div className="flex items-start space-x-4">
                    <FileText className="h-6 w-6 text-purple-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-burgundy-800 mb-2">Marco Normativo Institucional</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Creación de reglamentos universitarios que obliguen a incluir la salud y el bienestar
                        en planes estratégicos y presupuestos.
                      </p>
                      <ul className="text-xs space-y-1">
                        <li>• Políticas internas de promoción de la salud</li>
                        <li>• Integración en planes estratégicos institucionales</li>
                        <li>• Asignación presupuestaria específica</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-l-blue-500">
                  <div className="flex items-start space-x-4">
                    <Users className="h-6 w-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-burgundy-800 mb-2">Participación en Agenda Pública</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Representación de la universidad en consejos de salud, redes intersectoriales
                        y elaboración de políticas nacionales.
                      </p>
                      <ul className="text-xs space-y-1">
                        <li>• Consejos municipales y regionales de salud</li>
                        <li>• Redes intersectoriales de bienestar</li>
                        <li>• Mesas de trabajo en política pública</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-l-green-500">
                  <div className="flex items-start space-x-4">
                    <Globe className="h-6 w-6 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-burgundy-800 mb-2">Incidencia en Políticas Globales</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Alineación con los Objetivos de Desarrollo Sostenible (ODS), la Carta de Ottawa
                        y la Carta de Ginebra sobre el bienestar.
                      </p>
                      <ul className="text-xs space-y-1">
                        <li>• Implementación de ODS 3, 4 y 11</li>
                        <li>• Aplicación de principios de Ottawa</li>
                        <li>• Adherencia a estándares internacionales</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-burgundy-800 mb-6">
                Casos de Éxito e Indicadores
              </h3>

              <Card className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <Award className="h-6 w-6 text-green-600" />
                    <CardTitle className="text-green-800">Casos de Éxito</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border border-green-200">
                      <h5 className="font-semibold text-green-800 mb-2">Universidad modelo</h5>
                      <p className="text-sm text-gray-600 mb-2">
                        Universidad Autónoma de Nuevo León en proceso de implementación y acreditación del modelo de Universidades promotoras de la salud.
                      </p>
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <span className="text-xs text-green-700">Se prevé una mejora de ↗ 40% en indicadores de salud</span>
                      </div>
                    </div>

                  </div>
                </CardContent>
              </Card>

              <Card className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <Award className="h-6 w-6 text-green-600" />
                    <CardTitle className="text-green-800">Próximos programas</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border border-green-200">
                      <h5 className="font-semibold text-green-800 mb-2">Red Internacional UPS</h5>
                      <p className="text-sm text-gray-600 mb-2">
                        Forma parte de las Universidades Promotoras de la Salud.
                      </p>
                      <div className="flex items-center space-x-2">
                        <Target className="h-4 w-4 text-green-600" />
                        <span className="text-xs text-green-700">Política adoptada nacionalmente</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-burgundy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Integración de los Cuatro Pilares
            </h2>
            <p className="text-lg text-burgundy-100 max-w-4xl mx-auto">
              Los cuatro pilares no funcionan de manera aislada, sino como un sistema integral
              que potencia el impacto de cada componente en la promoción de políticas saludables
              y la transformación social universitaria.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="bg-burgundy-800 border-burgundy-700">
              <CardHeader className="text-center">
                <GraduationCap className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                <CardTitle className="text-white text-sm">Docencia Transformadora</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-burgundy-200 text-xs">
                  Integración curricular transversal de la promoción de la salud en todas las disciplinas
                </p>
              </CardContent>
            </Card>

            <Card className="bg-burgundy-800 border-burgundy-700">
              <CardHeader className="text-center">
                <Building2 className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <CardTitle className="text-white text-sm">Campus Saludable</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-burgundy-200 text-xs">
                  Entornos físicos y sociales que modelan prácticas saludables para la comunidad
                </p>
              </CardContent>
            </Card>

            <Card className="bg-burgundy-800 border-burgundy-700">
              <CardHeader className="text-center">
                <Heart className="h-8 w-8 text-rose-400 mx-auto mb-2" />
                <CardTitle className="text-white text-sm">Servicios Integrales</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-burgundy-200 text-xs">
                  Atención holística de la salud como modelo replicable en el territorio
                </p>
              </CardContent>
            </Card>

            <Card className="bg-burgundy-800 border-burgundy-700">
              <CardHeader className="text-center">
                <Scale className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                <CardTitle className="text-white text-sm">Incidencia Política</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-burgundy-200 text-xs">
                  Liderazgo en la formulación de políticas públicas de salud y bienestar
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-burgundy-800 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-center mb-6">Articulación en las Funciones Sustantivas</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-burgundy-700 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold mb-3">Docencia</h4>
                <p className="text-burgundy-200 text-sm">
                  Formación de profesionales con competencias en promoción de la salud
                  y capacidad de incidencia en políticas públicas.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-burgundy-700 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold mb-3">Investigación</h4>
                <p className="text-burgundy-200 text-sm">
                  Generación de evidencia científica para fundamentar políticas públicas
                  de salud universitaria y comunitaria.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-burgundy-700 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold mb-3">Extensión</h4>
                <p className="text-burgundy-200 text-sm">
                  Proyección social que transforma territorios y fortalece
                  sistemas de salud pública locales y regionales.
                </p>
              </div>
            </div>
          </div>
          <hr className="border-burgundy-700" />
        </div>
      </section>
    </div>
  );
};